import React, { useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  Menu,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { Add, Delete, Edit, HomeWork, MoreVert, VpnKey } from "@mui/icons-material";
import {
  useGetHandoversQuery,
  useCreateHandoverMutation,
  useUpdateHandoverMutation,
  useDeleteHandoverMutation,
  ProTrackHandover,
  HandoverStatus,
} from "../../../redux/protrackApi";
import { useGetComsBlocksQuery } from "../../../redux/comsApi";

const NAVY = "#123456";
const TODAY = new Date().toISOString().slice(0, 10);

const STATUS_CONFIG: Record<HandoverStatus, { label: string; color: string }> = {
  SCHEDULED: { label: "Scheduled", color: "#78909c" },
  INSPECTED: { label: "Inspected", color: "#1565c0" },
  SNAGGED: { label: "Snagged", color: "#f57c00" },
  SIGNED_OFF: { label: "Signed Off", color: "#2e7d32" },
  KEYS_HANDED: { label: "Keys Handed", color: "#1b5e20" },
};

const UNIT_TYPES = ["STUDIO", "1-BED", "2-BED", "3-BED", "PENTHOUSE", "TOWNHOUSE", "OTHER"];

const emptyForm = (): Partial<ProTrackHandover> => ({
  blockId: "",
  unitNumber: "",
  unitType: "",
  clientName: "",
  clientEmail: "",
  clientPhone: "",
  scheduledDate: "",
  inspectionDate: "",
  handoverDate: "",
  status: "SCHEDULED",
  snagCount: 0,
  outstandingSnags: 0,
  notes: "",
});

export const ProTrackHandovers: React.FC = () => {
  const { data: blocks = [] } = useGetComsBlocksQuery();
  const [blockFilter, setBlockFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const { data: handovers = [], isLoading } = useGetHandoversQuery({
    blockId: blockFilter || undefined,
    status: statusFilter || undefined,
  });
  const [createHandover, { isLoading: creating }] = useCreateHandoverMutation();
  const [updateHandover, { isLoading: updating }] = useUpdateHandoverMutation();
  const [deleteHandover] = useDeleteHandoverMutation();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<ProTrackHandover | null>(null);
  const [form, setForm] = useState<Partial<ProTrackHandover>>(emptyForm());
  const [formError, setFormError] = useState("");

  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [menuTarget, setMenuTarget] = useState<ProTrackHandover | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  const openCreate = () => {
    setEditTarget(null);
    setForm(emptyForm());
    setFormError("");
    setDialogOpen(true);
  };

  const openEdit = (h: ProTrackHandover) => {
    setMenuAnchor(null);
    setEditTarget(h);
    setForm({ ...h });
    setFormError("");
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.blockId) { setFormError("Select a project block."); return; }
    if (!form.unitNumber?.trim()) { setFormError("Unit number is required."); return; }
    setFormError("");
    const payload = {
      blockId: form.blockId!,
      unitNumber: form.unitNumber!,
      unitType: form.unitType || undefined,
      clientName: form.clientName || undefined,
      clientEmail: form.clientEmail || undefined,
      clientPhone: form.clientPhone || undefined,
      scheduledDate: form.scheduledDate || undefined,
      inspectionDate: form.inspectionDate || undefined,
      handoverDate: form.handoverDate || undefined,
      status: (form.status as HandoverStatus) ?? "SCHEDULED",
      snagCount: Number(form.snagCount ?? 0),
      outstandingSnags: Number(form.outstandingSnags ?? 0),
      notes: form.notes || undefined,
    };
    try {
      if (editTarget?.id) {
        await updateHandover({ id: editTarget.id, data: payload }).unwrap();
      } else {
        await createHandover(payload).unwrap();
      }
      setDialogOpen(false);
    } catch (err: any) {
      setFormError(err?.data?.error?.message ?? "Failed to save. Please try again.");
    }
  };

  const handleDelete = async () => {
    if (menuTarget?.id) await deleteHandover(menuTarget.id);
    setDeleteConfirmOpen(false);
    setMenuAnchor(null);
  };

  // Group by block
  const grouped = handovers.reduce<Record<string, ProTrackHandover[]>>((acc, h) => {
    const key = h.blockName ?? h.blockId;
    (acc[key] = acc[key] ?? []).push(h);
    return acc;
  }, {});

  return (
    <Box>
      {/* Toolbar */}
      <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2} mb={3}>
        <Box display="flex" gap={2} flexWrap="wrap">
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Filter by Block</InputLabel>
            <Select label="Filter by Block" value={blockFilter} onChange={(e) => setBlockFilter(e.target.value)}>
              <MenuItem value="">All Blocks</MenuItem>
              {blocks.map((b) => <MenuItem key={b.id} value={b.id}>{b.code} — {b.name}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Status</InputLabel>
            <Select label="Status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <MenuItem value="">All Statuses</MenuItem>
              {Object.entries(STATUS_CONFIG).map(([v, sc]) => <MenuItem key={v} value={v}>{sc.label}</MenuItem>)}
            </Select>
          </FormControl>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={openCreate}
          sx={{ bgcolor: NAVY, "&:hover": { bgcolor: "#1a4a7a" } }}
        >
          Add Handover
        </Button>
      </Box>

      {isLoading ? (
        <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
      ) : handovers.length === 0 ? (
        <Box textAlign="center" py={8} color="text.secondary">
          <HomeWork sx={{ fontSize: 48, opacity: 0.3 }} />
          <Typography mt={1}>No handover records found. Add the first one.</Typography>
        </Box>
      ) : (
        Object.entries(grouped).map(([groupName, items]) => (
          <Box key={groupName} mb={4}>
            <Typography variant="subtitle1" fontWeight={700} color={NAVY} mb={1}>
              {groupName}
            </Typography>
            <Box display="flex" flexWrap="wrap" gap={2}>
              {items.map((h) => {
                const sc = STATUS_CONFIG[h.status as HandoverStatus] ?? STATUS_CONFIG.SCHEDULED;
                return (
                  <Box
                    key={h.id}
                    sx={{
                      border: "1px solid #e0e0e0",
                      borderTop: `3px solid ${sc.color}`,
                      borderRadius: 2,
                      p: 2,
                      bgcolor: "#fff",
                      minWidth: 220,
                      flex: "1 1 220px",
                      maxWidth: 280,
                      position: "relative",
                      "&:hover": { boxShadow: "0 2px 8px rgba(0,0,0,0.1)" },
                    }}
                  >
                    <Box display="flex" alignItems="flex-start" justifyContent="space-between">
                      <Box>
                        <Typography fontWeight={700} fontSize={18}>
                          Unit {h.unitNumber}
                        </Typography>
                        {h.unitType && (
                          <Typography variant="caption" color="text.secondary">
                            {h.unitType}
                          </Typography>
                        )}
                      </Box>
                      <IconButton size="small" onClick={(e) => { setMenuAnchor(e.currentTarget); setMenuTarget(h); }}>
                        <MoreVert />
                      </IconButton>
                    </Box>
                    <Chip label={sc.label} size="small" sx={{ bgcolor: `${sc.color}22`, color: sc.color, fontWeight: 600, fontSize: 11, mt: 0.5 }} />
                    {h.clientName && (
                      <Typography variant="body2" mt={1}>{h.clientName}</Typography>
                    )}
                    <Box mt={1} display="flex" flexDirection="column" gap={0.25}>
                      {h.scheduledDate && (
                        <Typography variant="caption" color="text.secondary">
                          Scheduled: {h.scheduledDate}
                        </Typography>
                      )}
                      {h.inspectionDate && (
                        <Typography variant="caption" color="text.secondary">
                          Inspected: {h.inspectionDate}
                        </Typography>
                      )}
                      {h.handoverDate && (
                        <Typography variant="caption" color="text.secondary">
                          Handed: {h.handoverDate}
                        </Typography>
                      )}
                    </Box>
                    {h.snagCount > 0 && (
                      <Box display="flex" gap={1} mt={1}>
                        <Chip
                          label={`${h.snagCount} snags`}
                          size="small"
                          sx={{ bgcolor: "#fff3e0", color: "#e65100", fontSize: 10 }}
                        />
                        {h.outstandingSnags > 0 && (
                          <Chip
                            label={`${h.outstandingSnags} outstanding`}
                            size="small"
                            sx={{ bgcolor: "#ffebee", color: "#c62828", fontSize: 10 }}
                          />
                        )}
                      </Box>
                    )}
                    {h.status === "KEYS_HANDED" && (
                      <VpnKey sx={{ position: "absolute", bottom: 12, right: 12, color: "#1b5e20", fontSize: 18, opacity: 0.6 }} />
                    )}
                  </Box>
                );
              })}
            </Box>
          </Box>
        ))
      )}

      {/* Context Menu */}
      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
        <MenuItem onClick={() => menuTarget && openEdit(menuTarget)}>
          <Edit sx={{ mr: 1, fontSize: 18 }} /> Edit
        </MenuItem>
        <MenuItem sx={{ color: "error.main" }} onClick={() => { setDeleteConfirmOpen(true); setMenuAnchor(null); }}>
          <Delete sx={{ mr: 1, fontSize: 18 }} /> Delete
        </MenuItem>
      </Menu>

      {/* Delete Confirm */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)} maxWidth="xs">
        <DialogTitle>Delete Handover</DialogTitle>
        <DialogContent>
          <Typography>Delete unit <strong>{menuTarget?.unitNumber}</strong> handover record? This cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete}>Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Create / Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{editTarget ? "Edit Handover" : "Add Handover"}</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <FormControl fullWidth size="small" required>
              <InputLabel>Project Block *</InputLabel>
              <Select label="Project Block *" value={form.blockId ?? ""} onChange={(e) => setForm((f) => ({ ...f, blockId: e.target.value }))}>
                {blocks.map((b) => <MenuItem key={b.id} value={b.id}>{b.code} — {b.name}</MenuItem>)}
              </Select>
            </FormControl>
            <Box display="flex" gap={2}>
              <TextField label="Unit Number *" size="small" fullWidth value={form.unitNumber ?? ""} onChange={(e) => setForm((f) => ({ ...f, unitNumber: e.target.value }))} />
              <FormControl fullWidth size="small">
                <InputLabel>Unit Type</InputLabel>
                <Select label="Unit Type" value={form.unitType ?? ""} onChange={(e) => setForm((f) => ({ ...f, unitType: e.target.value }))}>
                  <MenuItem value=""><em>None</em></MenuItem>
                  {UNIT_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                </Select>
              </FormControl>
            </Box>
            <TextField label="Client Name" size="small" fullWidth value={form.clientName ?? ""} onChange={(e) => setForm((f) => ({ ...f, clientName: e.target.value }))} />
            <Box display="flex" gap={2}>
              <TextField label="Client Email" size="small" fullWidth value={form.clientEmail ?? ""} onChange={(e) => setForm((f) => ({ ...f, clientEmail: e.target.value }))} />
              <TextField label="Client Phone" size="small" fullWidth value={form.clientPhone ?? ""} onChange={(e) => setForm((f) => ({ ...f, clientPhone: e.target.value }))} />
            </Box>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select label="Status" value={form.status ?? "SCHEDULED"} onChange={(e) => setForm((f) => ({ ...f, status: e.target.value as HandoverStatus }))}>
                {Object.entries(STATUS_CONFIG).map(([v, sc]) => <MenuItem key={v} value={v}>{sc.label}</MenuItem>)}
              </Select>
            </FormControl>
            <Box display="flex" gap={2}>
              <TextField label="Scheduled Date" type="date" size="small" fullWidth InputLabelProps={{ shrink: true }} value={form.scheduledDate ?? ""} onChange={(e) => setForm((f) => ({ ...f, scheduledDate: e.target.value }))} />
              <TextField label="Inspection Date" type="date" size="small" fullWidth InputLabelProps={{ shrink: true }} value={form.inspectionDate ?? ""} onChange={(e) => setForm((f) => ({ ...f, inspectionDate: e.target.value }))} />
            </Box>
            <TextField label="Handover Date" type="date" size="small" sx={{ maxWidth: 200 }} InputLabelProps={{ shrink: true }} value={form.handoverDate ?? ""} onChange={(e) => setForm((f) => ({ ...f, handoverDate: e.target.value }))} />
            <Box display="flex" gap={2}>
              <TextField label="Snag Count" type="number" size="small" sx={{ maxWidth: 130 }} inputProps={{ min: 0 }} value={form.snagCount ?? 0} onChange={(e) => setForm((f) => ({ ...f, snagCount: Number(e.target.value) }))} />
              <TextField label="Outstanding Snags" type="number" size="small" sx={{ maxWidth: 160 }} inputProps={{ min: 0 }} value={form.outstandingSnags ?? 0} onChange={(e) => setForm((f) => ({ ...f, outstandingSnags: Number(e.target.value) }))} />
            </Box>
            <TextField label="Notes" size="small" fullWidth multiline rows={2} value={form.notes ?? ""} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} />
            {formError && <Typography color="error" variant="caption">{formError}</Typography>}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={creating || updating} sx={{ bgcolor: NAVY, "&:hover": { bgcolor: "#1a4a7a" } }}>
            {creating || updating ? <CircularProgress size={18} /> : editTarget ? "Save Changes" : "Add Handover"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
